import { useCallback, useRef, useState } from 'react';
import { Mui, MuiIcons } from '@osu/react-ui';

// Datagrid refresh control. Owns the refresh invocation, loading indicator, and duplicate-request
// prevention internally — feature grids only supply an awaitable `onRefresh` that reruns their exact
// current query, and (optionally) a `canRefresh` availability flag. While a refresh is in flight the
// button is disabled and shows a spinner so repeated clicks can't stack refetches. Mirrors the shared
// control used on the admissions-side grids so the button looks and behaves identically here.
export default function DataGridRefreshControl({ onRefresh, canRefresh = true, ariaLabel = 'Refresh data', testId = 'datagrid-refresh-button' }) {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const isRefreshingRef = useRef(false);

    const refresh = useCallback(async () => {
        if (!canRefresh || isRefreshingRef.current || typeof onRefresh !== 'function') { return; }

        isRefreshingRef.current = true;
        setIsRefreshing(true);

        try {
            await onRefresh();
        } catch {
            // Ignored - the grid's existing query/error state remains responsible for feedback.
        } finally {
            isRefreshingRef.current = false;
            setIsRefreshing(false);
        }
    }, [canRefresh, onRefresh]);

    const isDisabled = !canRefresh || isRefreshing || typeof onRefresh !== 'function';

    return (
        <Mui.Tooltip title={ariaLabel}>
            <span>
                <Mui.IconButton
                    aria-label={ariaLabel}
                    data-testid={testId}
                    disabled={isDisabled}
                    onClick={refresh}
                    size="small"
                >
                    {isRefreshing
                        ? <Mui.CircularProgress aria-label="Refreshing data" size={20} />
                        : <MuiIcons.Refresh />}
                </Mui.IconButton>
            </span>
        </Mui.Tooltip>
    );
}
